import { Status, USER_STATUS_TYPES_TO_CLASSNAME } from 'components/Status'
import { Link } from 'components/Link'
import { Dropdown } from 'components/Dropdown'
import { Sorter } from 'components/Sorter/Sorter'
import { DescriptionCell } from '../../../../components/DescriptionCell/DescriptionCell'
import { Avatar } from '../../../../components/Avatar/Avatar'
import { StartChat } from '../../../../components/StartChat/StartChat'
import { CellWithSubtitle } from 'components/CellWithSubtitle/CellWithSubtitle'
import {
  getContractCategoriesForCommonsTable,
  getContractCategoriesList
} from './api'
import { fullFormatPhoneNumber } from '../../../../helper/common'
import { ReactComponent as Filter16 } from 'assets/svg/Filter16.svg'
import { ReactComponent as FilterApplied16 } from 'assets/svg/FilterApplied16.svg'
import { MY_COMMUNITY_USER_STATUSES } from '../../constants'
import { routes } from 'router'
import { ACTIONS, ROLES, ability, SUBJECTS } from 'features/Permission'
import { ROLES_NAMES } from 'constants/common'
import { ColumnsType } from 'antd/es/table'
import { SortOrder } from 'antd/es/table/interface'
import {
  CommonsUser,
  CommonsUsersFiltersFunc,
  CommonsUsersColumnsFunc,
  CommonsUsersFilters
} from './types'

export const columns = ({
  applyFilters,
  filters,
  role,
  onStartChat,
  isNotAvailableForCCS
}: CommonsUsersColumnsFunc): ColumnsType<CommonsUser> => {
  const _columns: ColumnsType<CommonsUser> = [
    {
      dataIndex: 'first_name',
      key: 'first_name',
      className: 'avatar-cell',
      width: 44,
      title: null,
      render: (_, { first_name, avatar_logo, last_name }) => (
        <Avatar
          name={`${first_name[0]?.toUpperCase()}${last_name[0]?.toUpperCase()}`}
          src={avatar_logo?.file_preview || ''}
          alt="user image"
        />
      ),
      sorter: false,
      showSorterTooltip: false
    },
    {
      dataIndex: 'name',
      key: 'name',
      width: 280,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<CommonsUser>
          dataIndex="name"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          User name
        </Sorter>
      ),
      render: (_, { first_name, last_name, uuid }) =>
        (ability.can(ACTIONS.VIEW, SUBJECTS.COMMONS_USERS) ||
          ability.can(ACTIONS.CRUD, SUBJECTS.COMMONS_USERS)) &&
        !isNotAvailableForCCS ? (
          <CellWithSubtitle
            title={
              <Link
                to={`${routes.createUserProfile}/${uuid}?fromCommunity=true`}
              >
                {first_name} {last_name}
              </Link>
            }
          />
        ) : (
          `${first_name} ${last_name}`
        ),
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'status',
      key: 'status',
      width: 130,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<CommonsUser>
          dataIndex="status"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Status
        </Sorter>
      ),
      filterDropdown: (props) => (
        <Dropdown.Filter<CommonsUsersFilters, CommonsUsersFiltersFunc, unknown>
          items={MY_COMMUNITY_USER_STATUSES}
          applyFilters={applyFilters}
          appliedFilters={filters}
          field="statuses"
          {...props}
        />
      ),
      render: (_, { status, uuid }) => (
        <div className="row gap-8 align-center">
          <Status.Default className={USER_STATUS_TYPES_TO_CLASSNAME[status]}>
            {status}
          </Status.Default>
          {status === 'Active' && (
            <StartChat onClick={() => onStartChat(uuid)} />
          )}
        </div>
      ),
      filterIcon: !!filters?.statuses?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true,
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'email',
      key: 'email',
      width: 240,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<CommonsUser>
          dataIndex="email"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Email
        </Sorter>
      ),
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'phone_number',
      key: 'phone_number',
      title: 'Phone #',
      width: 196,
      render: (_, { phone_number }) =>
        phone_number ? fullFormatPhoneNumber(phone_number) : '-',
      sorter: false,
      showSorterTooltip: false
    },
    {
      dataIndex: 'contract_categories',
      key: 'contract_categories',
      title: 'Contract categories',
      filterDropdown: (props) => (
        <Dropdown.Filter<CommonsUsersFilters, CommonsUsersFiltersFunc, unknown>
          applyFilters={applyFilters}
          appliedFilters={filters}
          searchCallback={() =>
            role
              ? getContractCategoriesList(role)
              : getContractCategoriesForCommonsTable()
          }
          field="contract_categories"
          asyncSearch
          {...props}
        />
      ),
      render: (_, { contract_categories }) => {
        const text = contract_categories.map((i) => i.name).join(', ')
        return <DescriptionCell content={text} text={text} />
      },
      filterIcon: !!filters?.contract_categories?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true,
      showSorterTooltip: false
    }
  ]

  if (!role) {
    _columns.unshift({
      dataIndex: 'role',
      key: 'role',
      width: 180,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<CommonsUser>
          dataIndex="role"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Role
        </Sorter>
      ),
      render: (text) => ROLES_NAMES[text] ?? <>–</>,
      sorter: true,
      showSorterTooltip: false
    })
  }

  return _columns
}

export const AVAILABLE_FOR_CCS = [
  ROLES.COMMONS_ANALYST,
  ROLES.COMMONS_STAKEHOLDER,
  ROLES.COMMONS_LEGAL
]
